import { I4BCellType, I4BColumnConfiguration, I4BColumnRowGroupConfiguration } from '../configurations';
import { I4bCellConcept } from '../enums';
import { HeaderType, I4BColumnHeader } from '../models';
import { I4BVariableColumnOptions } from '../options';
import { I4BBasicColumn } from './i4b-basic.column';
import { RichVariableColumn } from './i4b-rich-variable.column';
import { I4BThresholdColumn } from './i4b-threshold.column';

const defaultDisplayThresholds = { 1: false, 2: false, 3: false, 4: false, 5: false };

interface AssetVariableGroupColumnConfiguration extends I4BColumnConfiguration {
  rowGroupParams: Partial<{
    value: Partial<I4BColumnRowGroupConfiguration>;
    dateTime: Partial<I4BColumnRowGroupConfiguration>;
    threshold1: Partial<I4BColumnRowGroupConfiguration>;
    threshold2: Partial<I4BColumnRowGroupConfiguration>;
    threshold3: Partial<I4BColumnRowGroupConfiguration>;
    threshold4: Partial<I4BColumnRowGroupConfiguration>;
    threshold5: Partial<I4BColumnRowGroupConfiguration>;
  }>;
}

export class AssetVariableGroupColumn extends I4BBasicColumn {
  columnId = '19bcb62c-724b-11ec-a844-acde48001122-asset-variable-group';
  className = AssetVariableGroupColumn;

  constructor(
    customHeader: Partial<I4BColumnHeader> | undefined,
    customConfig: Partial<AssetVariableGroupColumnConfiguration>,
    customOptions: Partial<I4BVariableColumnOptions>
  ) {
    super(
      {
        ...customHeader,
        displayName: customHeader?.displayName ?? 'VARIABLE',
        type: HeaderType.BASIC
      },
      {
        cell: { type: I4BCellType.BASIC },
        id: customConfig.id,
        // TODO the check on 'GROUPED VARIABLES' should be removed once the migration in server side is done
        // The GROUPED VARIABLES concept was removed and replaced by STANDARD_VARIABLES
        concept:
          customConfig?.concept && (customConfig?.concept as any)?.toLowerCase() !== 'GROUPED VARIABLES'.toLowerCase()
            ? customConfig?.concept
            : I4bCellConcept.STANDARD_VARIABLES,
        isDefault: false,
        marryChildren: true,
        rowGroupParams: customConfig?.rowGroupParams,
        children: [
          new RichVariableColumn(
            { displayName: 'VALUE' },
            {
              id: 'expandedVariables.' + customConfig.id,
              isDefault: true,
              sortProperty: 'expandedVariables.' + customConfig.id + 'lastValue.value',
              cell: {
                type: I4BCellType.RICH_VARIABLE,
                options: { dataType: 'lastValue', unit: 'unit', value: 'value', tooltip: 'datetime' },
                event: { type: 'openChartByVariable', options: { variableName: customConfig.id } },
                valueGetter: 'expandedVariables.' + customConfig.id + '.lastValue.value'
              },
              rowGroupParams: customConfig?.rowGroupParams?.value
            },
            { order: 0, customId: 'expandedVariables.' + customConfig.id, sortable: true, isDisplayed: true }
          ),
          new I4BBasicColumn(
            { displayName: 'DATETIME' },
            {
              id: 'expandedVariables.' + customConfig.id + '.lastValue.datetime',
              cell: { type: I4BCellType.DATE },
              rowGroupParams: customConfig?.rowGroupParams?.dateTime
            },
            { order: 1, customId: 'expandedVariables.' + customConfig.id, isDisplayed: customOptions.displayDateTime }
          ),
          new I4BThresholdColumn(
            {},
            {
              id: customConfig.id,
              cell: { type: I4BCellType.ASSET_VARIABLE_THRESHOLD_CELL, options: { position: 1 } },
              rowGroupParams: customConfig?.rowGroupParams?.threshold1
            },
            {
              order: 3,
              displayThresholds: customOptions.displayThresholds
            }
          ),
          new I4BThresholdColumn(
            {},
            {
              id: customConfig.id,
              cell: { type: I4BCellType.ASSET_VARIABLE_THRESHOLD_CELL, options: { position: 2 } },
              rowGroupParams: customConfig?.rowGroupParams?.threshold2
            },
            {
              order: 4,
              displayThresholds: customOptions.displayThresholds
            }
          ),
          new I4BThresholdColumn(
            {},
            {
              id: customConfig.id,
              cell: { type: I4BCellType.ASSET_VARIABLE_THRESHOLD_CELL, options: { position: 3 } },
              rowGroupParams: customConfig?.rowGroupParams?.threshold3
            },
            {
              order: 5,
              displayThresholds: customOptions.displayThresholds
            }
          ),
          new I4BThresholdColumn(
            {},
            {
              id: customConfig.id,
              cell: { type: I4BCellType.ASSET_VARIABLE_THRESHOLD_CELL, options: { position: 4 } },
              rowGroupParams: customConfig?.rowGroupParams?.threshold4
            },
            {
              order: 6,
              displayThresholds: customOptions.displayThresholds
            }
          ),
          new I4BThresholdColumn(
            {},
            {
              id: customConfig.id,
              cell: { type: I4BCellType.ASSET_VARIABLE_THRESHOLD_CELL, options: { position: 5 } },
              rowGroupParams: customConfig?.rowGroupParams?.threshold5
            },
            {
              order: 7,
              displayThresholds: customOptions.displayThresholds
            }
          )
        ]
      },
      {
        displayDateTime: true,
        displayThresholds: defaultDisplayThresholds,
        ...customOptions,
        customId: customConfig.id ?? undefined
      }
    );
  }
}
